import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Auth } from 'aws-amplify';
import { faEdit, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CentralCalls from '../../centralCalls';

const WipFiles = ({ selectedLotOid }) => {
  const [userName, setUserName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingFile, setIsPreparingFile] = useState(false);
  const [editingDescriptionOid, setEditingDescriptionOid] = useState(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [phaseOidDraft, setPhaseOidDraft] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [phaseOptions, setPhaseOptions] = useState([]);
  const [phaseTypeOptions, setPhaseTypeOptions] = useState([]);
  const [inlineImageUrls, setInlineImageUrls] = useState({});

  const loadFiles = useCallback(async () => {
    if (!selectedLotOid) {
      setFiles([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await CentralCalls.fetchTableData('files', { lot_oid: selectedLotOid, is_deleted: 0 });
      const rows = Array.isArray(data) ? [...data] : [];
      rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setFiles(rows);
    } catch (error) {
      console.error(error);
      alert('Unable to load files. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedLotOid]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    const loadPhases = async () => {
      if (!selectedLotOid) {
        setPhaseOptions([]);
        return;
      }

      try {
        const rows = await CentralCalls.fetchTableData('phase', { lot_oid: selectedLotOid });
        const sortedRows = Array.isArray(rows)
          ? [...rows].sort((a, b) => {
            const dateA = a?.date_scheduled || '';
            const dateB = b?.date_scheduled || '';
            return dateA.localeCompare(dateB);
          })
          : [];
        setPhaseOptions(sortedRows);
      } catch (error) {
        console.error(error);
        setPhaseOptions([]);
      }
    };

    loadPhases();
  }, [selectedLotOid]);

  useEffect(() => {
    const loadPhaseTypes = async () => {
      try {
        const rows = await CentralCalls.fetchTableDataNoWhere('phase_type');
        setPhaseTypeOptions(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error(error);
        setPhaseTypeOptions([]);
      }
    };

    loadPhaseTypes();
  }, []);

  useEffect(() => {
    async function setUserInfo() {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setUserName(user?.attributes?.email || user?.username || 'unknown');
      } catch (error) {
        console.error('Error fetching user info', error);
      }
    }

    setUserInfo();
  }, []);

  const handleUpload = async () => {
    if (!selectedLotOid || !selectedFile) {
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('lot_oid', selectedLotOid);
    formData.append('uploaded_by', userName || 'unknown');

    setIsUploading(true);
    try {
      const response = await fetch('/api/files/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Upload failed.');
      }

      setSelectedFile(null);
      const fileInput = document.getElementById('wip-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
      await loadFiles();
    } catch (error) {
      console.error(error);
      alert(error.message || 'Unable to upload file.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      setSelectedFile(null);
      setIsPreparingFile(false);
      return;
    }

    setIsPreparingFile(true);
    setSelectedFile(null);

    window.setTimeout(() => {
      setSelectedFile(file);
      setIsPreparingFile(false);
    }, 200);
  };

  const handleDownload = async (fileOid, fileName) => {
    try {
      const response = await fetch(`/api/files/${fileOid}/download`);
      if (!response.ok) {
        throw new Error('Download failed.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert('Unable to download file.');
    }
  };

  const handleDeleteFile = async (fileOid, fileName) => {
    const shouldDelete = window.confirm(`Delete file "${fileName}"?`);
    if (!shouldDelete) {
      return;
    }

    try {
      const result = await CentralCalls.upsertData({ oid: fileOid, is_deleted: 1 }, 'files', 'update');
      if (!result) {
        throw new Error('Delete failed.');
      }

      await loadFiles();
    } catch (error) {
      console.error(error);
      alert(error.message || 'Unable to delete file.');
    }
  };

  const startDescriptionEdit = (fileRow) => {
    setEditingDescriptionOid(fileRow.oid);
    setDescriptionDraft(fileRow.description || '');
    setPhaseOidDraft(fileRow.phase_oid ? String(fileRow.phase_oid) : '');
  };

  const cancelDescriptionEdit = () => {
    setEditingDescriptionOid(null);
    setDescriptionDraft('');
    setPhaseOidDraft('');
  };

  const formatPhaseOptionDate = (dateValue) => {
    if (!dateValue) {
      return 'open';
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return String(dateValue);
    }

    return parsedDate.toLocaleDateString();
  };

  const getPhaseDisplayValue = (phaseOid) => {
    if (!phaseOid) {
      return '';
    }

    const matchedPhase = phaseOptions.find((phaseRow) => String(phaseRow.oid) === String(phaseOid));
    if (!matchedPhase) {
      return `Phase ${phaseOid}`;
    }

    const phaseType = getPhaseTypeName(matchedPhase.phase_type);
    const phaseDate = formatPhaseOptionDate(matchedPhase.date_scheduled);
    return `${phaseType} - ${phaseDate}`;
  };

  const getPhaseTypeName = useCallback((phaseTypeValue) => {
    if (!phaseTypeValue) {
      return 'Unknown phase';
    }

    const matchedPhaseType = phaseTypeOptions.find((phaseTypeRow) => (
      String(phaseTypeRow.name_id) === String(phaseTypeValue)
      || String(phaseTypeRow.oid) === String(phaseTypeValue)
    ));

    if (!matchedPhaseType) {
      return String(phaseTypeValue);
    }

    return matchedPhaseType.name || matchedPhaseType.display_name || String(phaseTypeValue);
  }, [phaseTypeOptions]);

  const saveDescription = async (fileOid) => {
    setIsSavingDescription(true);
    try {
      const descriptionValue = descriptionDraft && descriptionDraft.trim() !== '' ? descriptionDraft.trim() : null;
      const phaseValue = phaseOidDraft !== '' ? phaseOidDraft : null;
      const result = await CentralCalls.upsertData(
        { oid: fileOid, description: descriptionValue, phase_oid: phaseValue },
        'files',
        'update',
      );
      if (!result) {
        throw new Error('Failed to update description.');
      }

      setEditingDescriptionOid(null);
      setDescriptionDraft('');
      setPhaseOidDraft('');
      await loadFiles();
    } catch (error) {
      console.error(error);
      alert(error.message || 'Unable to update description.');
    } finally {
      setIsSavingDescription(false);
    }
  };

  const getFilePhaseTypeLabel = useCallback((fileRow) => {
    if (!fileRow?.phase_oid) {
      return 'No phase assigned';
    }

    const matchedPhase = phaseOptions.find((phaseRow) => String(phaseRow.oid) === String(fileRow.phase_oid));
    if (!matchedPhase) {
      return 'Unknown phase';
    }

    return getPhaseTypeName(matchedPhase.phase_type);
  }, [getPhaseTypeName, phaseOptions]);

  const isImageFile = useCallback((fileRow) => {
    const lowerName = String(fileRow?.file_name || '').toLowerCase();
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg', '.heic', '.heif'];
    return imageExtensions.some((extension) => lowerName.endsWith(extension));
  }, []);

  useEffect(() => {
    let canceled = false;
    const createdUrls = [];

    const loadInlineImages = async () => {
      setInlineImageUrls((prev) => {
        Object.values(prev).forEach((url) => window.URL.revokeObjectURL(url));
        return {};
      });

      const imageFiles = files.filter((fileRow) => isImageFile(fileRow));
      if (imageFiles.length === 0) {
        return;
      }

      const results = await Promise.all(
        imageFiles.map(async (fileRow) => {
          const fileOid = Number(fileRow?.oid);
          if (!Number.isFinite(fileOid) || fileOid <= 0) {
            return null;
          }

          try {
            const response = await fetch(`/api/files/${fileOid}/download`, { method: 'GET' });
            if (!response.ok) {
              return null;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            createdUrls.push(url);
            return { fileOid, url };
          } catch {
            return null;
          }
        }),
      );

      if (canceled) {
        createdUrls.forEach((url) => window.URL.revokeObjectURL(url));
        return;
      }

      const nextUrls = {};
      results.forEach((result) => {
        if (result?.fileOid && result?.url) {
          nextUrls[result.fileOid] = result.url;
        }
      });

      setInlineImageUrls(nextUrls);
    };

    loadInlineImages();

    return () => {
      canceled = true;
      createdUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [files, isImageFile]);

  const groupedFiles = useMemo(() => {
    const groups = {};

    files.forEach((fileRow) => {
      const phaseTypeLabel = getFilePhaseTypeLabel(fileRow);
      if (!groups[phaseTypeLabel]) {
        groups[phaseTypeLabel] = [];
      }
      groups[phaseTypeLabel].push(fileRow);
    });

    return Object.entries(groups)
      .sort(([labelA], [labelB]) => {
        if (labelA === 'No phase assigned') {
          return 1;
        }
        if (labelB === 'No phase assigned') {
          return -1;
        }
        return labelA.localeCompare(labelB);
      });
  }, [files, getFilePhaseTypeLabel]);

  return (
    <>
      <h2>Files</h2>
      {selectedLotOid ? (
        <div className="notes-container">
          <div className="add-note-container">
            <input
              id="wip-file-input"
              type="file"
              onChange={handleFileChange}
              className="note-input"
            />
            <button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading || isPreparingFile}
              className="add-note-button"
            >
              {isPreparingFile ? 'Preparing...' : isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>

          {isPreparingFile && (
            <div className="file-status-row">
              <span className="loading-spinner" />
              <span>Loading selected file...</span>
            </div>
          )}

          {!isPreparingFile && selectedFile && (
            <div className="file-status-row">
              <span>Selected: {selectedFile.name}</span>
            </div>
          )}

          {isLoading && <p>Loading files...</p>}

          {!isLoading && files.length === 0 && <p>No files uploaded for this lot.</p>}

          {!isLoading && groupedFiles.map(([phaseTypeLabel, phaseFiles]) => (
            <section key={phaseTypeLabel} className="file-phase-group">
              <h3 className="file-phase-subtitle">{phaseTypeLabel}</h3>

              <div className="file-phase-grid">
                {phaseFiles.map((fileRow) => (
                  <div className="card file-card" key={fileRow.oid}>
                    {isImageFile(fileRow) && inlineImageUrls[fileRow.oid] && (
                      <div className="file-preview-wrap">
                        <img
                          src={inlineImageUrls[fileRow.oid]}
                          alt={fileRow.file_name || 'Uploaded file'}
                          className="file-preview-image"
                          loading="lazy"
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleDownload(fileRow.oid, fileRow.file_name)}
                      className="file-link-button"
                    >
                      Download: {fileRow.file_name}
                    </button>

                    <div className="file-card-actions">
                      <button
                        type="button"
                        className="file-icon-button"
                        onClick={() => startDescriptionEdit(fileRow)}
                        title="Edit description"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        type="button"
                        className="file-icon-button file-icon-delete"
                        onClick={() => handleDeleteFile(fileRow.oid, fileRow.file_name)}
                        title="Delete file"
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </div>

                    {fileRow.description && String(fileRow.description).trim() !== '' && (
                      <p className="file-description">{fileRow.description}</p>
                    )}

                    {fileRow.phase_oid && (
                      <p className="file-description">Phase: {getPhaseDisplayValue(fileRow.phase_oid)}</p>
                    )}

                    {editingDescriptionOid === fileRow.oid && (
                      <div className="file-description-editor">
                        <input
                          type="text"
                          className="note-input"
                          value={descriptionDraft}
                          onChange={(event) => setDescriptionDraft(event.target.value)}
                          placeholder="Add description2"
                          maxLength="500"
                        />
                        <select
                          className="note-input"
                          value={phaseOidDraft}
                          onChange={(event) => setPhaseOidDraft(event.target.value)}
                          disabled={isSavingDescription}
                        >
                          <option value="">No phase</option>
                          {phaseOptions.map((phaseRow) => (
                            <option key={phaseRow.oid} value={String(phaseRow.oid)}>
                              {getPhaseTypeName(phaseRow.phase_type)}
                              {' - '}
                              {formatPhaseOptionDate(phaseRow.date_scheduled)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="save-note-button"
                          disabled={isSavingDescription}
                          onClick={() => saveDescription(fileRow.oid)}
                        >
                          {isSavingDescription ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="add-note-button"
                          onClick={cancelDescriptionEdit}
                          disabled={isSavingDescription}
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    <p className="file-meta-row">
                      <span className="note-user">By: {fileRow.uploaded_by || 'Unknown'}</span>
                      <span className="note-date">Date: {fileRow.created_at ? new Date(fileRow.created_at).toLocaleDateString() : ''}</span>
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <p>No files available. Please select a lot.</p>
      )}
    </>
  );
};

export default WipFiles;
